import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  Divider, 
  List, 
  ListItem, 
  ListItemText,
  Grid
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Deal } from './DealPieChart';

interface RecentDealsListProps {
  deals: Deal[];
  onViewAll: () => void;
}

// Format date function
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'No date';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return 'Invalid date';
  }
};

// Check if date is within 7 days from current date
export const isWithinSevenDays = (dateString: string | null): boolean => {
  if (!dateString) return false;
  
  try {
    const dateToCheck = new Date(dateString);
    const today = new Date();
    
    // Reset hours to ensure we're comparing full days
    today.setHours(0, 0, 0, 0);
    
    // Calculate difference in milliseconds
    const diffTime = dateToCheck.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Check if date is within 7 days ahead
    return diffDays >= 0 && diffDays <= 7;
  } catch (error) {
    return false;
  }
};

// Function to render status chip
export const renderStatusChip = (bfsStage: string) => {
  if (!bfsStage || bfsStage === 'N/A') {
    return <Typography variant="caption" sx={{ color: 'text.secondary' }}>Not Started</Typography>;
  }
  
  switch (bfsStage.toLowerCase()) {
    case 'submitted':
      return <Typography variant="caption" sx={{ color: 'info.main' }}>Submitted</Typography>;
    case 'in progress':
      return <Typography variant="caption" sx={{ color: 'warning.main' }}>In Progress</Typography>;
    case 'completed':
      return <Typography variant="caption" sx={{ color: 'success.main' }}>Completed</Typography>;
    default:
      return <Typography variant="caption" sx={{ color: 'primary.main' }}>{bfsStage}</Typography>;
  }
};

const RecentDealsList: React.FC<RecentDealsListProps> = ({ deals, onViewAll }) => {
  // Sort deals by recency based on closing_date or cof date
  const sortedDeals = [...deals].sort((a, b) => {
    // Use the COF date if available, otherwise use closing_date
    const dateA = a.cof || a.closing_date || a.submission_date;
    const dateB = b.cof || b.closing_date || b.submission_date;
    
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });
  
  return (
    <Card elevation={3} sx={{ height: '100%' }}>
      <CardHeader 
        title="Recent Deals" 
        titleTypographyProps={{ variant: 'h6' }}
        avatar={<AssignmentIcon color="primary" />}
        action={
          <Button size="small" onClick={onViewAll}>
            View All
          </Button>
        }
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {sortedDeals.length > 0 ? (
          <List disablePadding>
            {sortedDeals.slice(0, 10).map((deal, index) => {
              const isClosingSoon = isWithinSevenDays(deal.closing_date);
              
              return (
                <React.Fragment key={deal.id || index}>
                  <ListItem sx={{ height: '5px', py: 0, px: 0.3, my: 0 }}>
                    <ListItemText
                      primary={
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={4}>
                            <Typography variant="body2" noWrap fontWeight="medium" sx={{ fontSize: '0.5rem' }}>
                              #{deal.deal_number}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Box display="flex" alignItems="center">
                              <CalendarTodayIcon sx={{ fontSize: '0.45rem', mr: 0.2, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.45rem', whiteSpace: 'nowrap' }}>
                                COF: {formatDate(deal.cof)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box display="flex" alignItems="center">
                              <CalendarTodayIcon 
                                sx={{ fontSize: '0.45rem', mr: 0.2, color: isClosingSoon ? 'error.main' : 'text.secondary' }} 
                              />
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: isClosingSoon ? 'error.main' : 'text.secondary', 
                                  fontWeight: isClosingSoon ? 'bold' : 'normal',
                                  fontSize: '0.45rem',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                Closing: {formatDate(deal.closing_date)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      }
                    />
                  </ListItem>
                  {index < 9 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Box p={3} display="flex" justifyContent="center">
            <Typography variant="body1" color="text.secondary">
              No deals available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentDealsList; 